import React, { useState } from "react";
import { Navigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { TrendingUp, Shield, Users, MessageSquare } from "lucide-react";

const Login = () => {
  const [params] = useSearchParams();
  const destination = params.get('next') === '/instructor-signup' ? '/instructor-signup' : '/productivity-coaching';
  const { user, login } = useAuth();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) {
    return <Navigate to={destination} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const result = await login(formData.email, formData.password);

    if (!result.success) {
      setError(result.error);
    }

    setLoading(false);
  };

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <div
      id="login-page"
      className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8"
      style={{
        background:
          "linear-gradient(135deg, var(--brand-primary) 0%, var(--brand-secondary) 100%)",
      }}
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        ></div>
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Main Login Card */}
        <div className="brand-card p-8 brand-shadow-lg">
          <div id="login-header" className="text-center mb-8">
            {/* Brand Logo */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 brand-gradient rounded-2xl flex items-center justify-center brand-shadow-lg">
                <TrendingUp className="w-8 h-8 text-white" />
              </div>
            </div>
            <h1 className="hellopc-wordmark-large text-center mb-2 hellopc-logo">
              <span className="hello">Hello</span>
              <span className="pc">PC</span>
            </h1>
            <h2 className="text-xl font-semibold text-slate-900 mt-4">
              Coach sign in
            </h2>
          </div>
          <Link
            id="coach-login-agent-entry"
            to="/agent/login"
            className="mb-6 min-h-11 w-full inline-flex items-center justify-center rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm font-semibold text-blue-700 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Are you an agent? Email me a sign-in link
          </Link>
          <form id="login-form" className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div
                id="login-error"
                role="alert"
                className="rounded-lg bg-red-50 p-4 border border-red-200"
              >
                <div className="text-sm text-red-700 font-medium">{error}</div>
              </div>
            )}

            <div id="login-fields" className="space-y-5">
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-semibold text-slate-700 mb-2"
                >
                  Email Address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  className="block w-full px-4 py-3 border border-slate-300 placeholder-slate-400 text-slate-900 rounded-lg brand-focus hover:border-slate-400 brand-transition"
                  placeholder="coach@example.com"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-semibold text-slate-700 mb-2"
                >
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  className="block w-full px-4 py-3 border border-slate-300 placeholder-slate-400 text-slate-900 rounded-lg brand-focus hover:border-slate-400 brand-transition"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div id="login-submit">
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-4 brand-gradient text-white font-semibold rounded-lg brand-transition brand-hover-lift brand-focus disabled:opacity-50 disabled:transform-none"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    Signing in...
                  </div>
                ) : (
                  "Sign In to Dashboard"
                )}
              </button>
            </div>

            <div className="text-center">
              <Link
                to="/forgot-password"
                className="text-sm text-blue-600 hover:text-blue-700 underline font-medium"
              >
                Forgot your password?
              </Link>
            </div>
            <div className="text-center">
              <span className="text-sm text-slate-500">
                Need a workspace?
              </span>
              <Link
                to="/coach-signup"
                className="ml-2 text-sm text-blue-600 hover:text-blue-700 underline font-semibold"
              >
                Create one
              </Link>
            </div>
          </form>
        </div>

        {/* Features Preview */}
        <div className="mt-8 grid grid-cols-1 gap-4">
          <div className="text-center">
            <h3 className="text-white font-semibold mb-4">
              Your Productivity Coaching Sidekick to help you:
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <p className="text-xs text-white opacity-90">Stay in Touch</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <MessageSquare className="w-6 h-6 text-white" />
                </div>
                <p className="text-xs text-white opacity-90">
                  Hold Accountable
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <p className="text-xs text-white opacity-90">Track Training</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
